import { useState } from "react";
import useGetTools from "src/hooks/useGetToolsBasedOnLanguage";
import useGetStacks from "src/hooks/useGetStacks";
import { useClientSignUpSteps } from "src/lib/store/hirer-steps-store";
import { StepStyleSection } from "../../layout";
import LoadingSpinner from "src/components/Loader/LoadingSpinner";
import ErrorBox from "../../components/ErrorBox";
import { CheckboxItem } from "../../components/check/checkbox";
import ButtonSpinner from "src/components/Loader/ButtonSpinner";
import EmptypBox from "../../components/EmptyBox";
import { toast } from "react-toastify";
import { useEffect } from "react";
import NewTool from "./NewTool";
import AddNewTool from "./AddNewTool";

export default function SelectTools({
  errorMessage
}) {
  const { userFrameworks, updateUserFrameworks, userLanguages, userStack, userJobType } = useClientSignUpSteps();
  const frameworks = userFrameworks ?? []; // fallback to empty array
  const frameworksLength = frameworks.length;
  const maxSelection = 3;

  const { stacks } = useGetStacks();
  const isCustomStack = userStack && userStack !== 'other' && stacks && stacks.length > 0 && !stacks.some(s => s.id === userStack);

  const { loading, error, tools, retry } = useGetTools(userLanguages);

  const [showOtherInput, setShowOtherInput] = useState(isCustomStack || userJobType === 'non-tech');
  const [showAddOtherInput, setShowAddOtherInput] = useState(userStack === 'other');

  useEffect(() => {
    if (userStack === 'other') {
      setShowAddOtherInput(true);
      setShowOtherInput(false);
    } else {
      setShowAddOtherInput(false);
    }
  }, [userStack]);
  
  useEffect(() => {
    if (isCustomStack) {
      setShowOtherInput(true);
    }
    // Ensure input shows for non‑tech role
    if (userJobType === 'non-tech') {
      setShowOtherInput(true);
    }
  }, [isCustomStack, userJobType]);

  useEffect(() => {
    if (!loading && (error || tools.length === 0) && userLanguages.length > 0 && userStack !== 'other') {
      setShowOtherInput(true);
    }
  }, [loading, error, tools, userLanguages, userStack]);

  return (
    <StepStyleSection isInvalid={errorMessage != null}>
      <div className="flex flex-col gap-4">
        {/* Header with Counter */}
        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
          <p className="text-semibold ">
          { showAddOtherInput ? "Add Tools" : "Select Tools" }</p>
            
          <div className="text-xs text-bold px-2 py-1 rounded-lg">
            {frameworksLength} / {maxSelection}
          </div>
        </div>

        {loading && <LoadingSpinner />}

        {!loading && error && !isCustomStack && !error.toString().includes('null') && !showAddOtherInput && (
          <ErrorBox message={`${error}`} onRetry={retry} />
        )}

        {!loading && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2 items-center">
              <CheckboxItem
                key={'other'}
                selected={showOtherInput}
                className={`${showAddOtherInput ? "bg-brand-navy text-white" : "border border-brand-navy bg-white text-brand-navy"} hidden`}
                onSelect={() => {
                  setShowOtherInput(false);
                  setShowAddOtherInput(!showAddOtherInput);
                }}
              >
                Other
              </CheckboxItem>

              {/* Render Custom Selected Tools */}
              {frameworks.filter(t => t.id.toString().startsWith('custom-')).map((customTool) => (
                <CheckboxItem
                  key={customTool.id}
                  selected={true}
                  onSelect={() => {
                    updateUserFrameworks(frameworks.filter(each => each.id !== customTool.id))
                  }}
                >
                  {customTool.name}
                </CheckboxItem>
              ))}

              {!error && !showAddOtherInput && tools.map((tool) => {
                const selected = frameworks.find(each => each.id === tool.id)

                return (
                  <CheckboxItem
                    key={tool.id}
                    selected={selected}
                    onSelect={() => {
                      if (selected) {
                        updateUserFrameworks(frameworks.filter(each => each.id !== selected?.id))
                      } else if ((frameworks?.length ?? 0) === maxSelection) {
                        toast.info('You have selected maximum number of Tools')
                      } else {
                        updateUserFrameworks([
                          ...frameworks,
                          {
                            id: tool.id,
                            experience: undefined,
                            name: tool?.name,
                          }
                        ])
                      }
                    }}
                  >
                    {tool.name}
                  </CheckboxItem>
                );
              })}
            </div>

            {!error && tools.length === 0 && !isCustomStack && (
              <EmptypBox message={"No tools found"} />
            )}

            {isCustomStack && (
              <div className="mt-2">
                {showOtherInput && <NewTool isNested={true} />}
              </div>
            )}
            {!showOtherInput && showAddOtherInput && <AddNewTool />}
          </div>
        )}
      </div>
    </StepStyleSection>
  );
}
